import React, { useEffect, useState } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { DEFAULT_HOMEPAGE_STUDIO } from '../utils/homepage';

function getStudioValues(studio) {
  return { ...DEFAULT_HOMEPAGE_STUDIO, ...(studio || {}) };
}

export default function StudioEditor({ studio: initialStudio, onSave }) {
  const [studio, setStudio] = useState(() => getStudioValues(initialStudio));
  const [savedStudio, setSavedStudio] = useState(() => getStudioValues(initialStudio));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const values = getStudioValues(initialStudio);
    setStudio(values);
    setSavedStudio(values);
  }, [initialStudio]);

  const updateField = (field, value) => {
    setStudio((current) => ({ ...current, [field]: value }));
    setError('');
    setNotice('');
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setError('');
    setNotice('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch('/api/uploads', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Falha ao enviar a imagem.');
      setStudio((current) => ({ ...current, image_url: data.url }));
    } catch (uploadError) {
      setError(uploadError.message || 'Não foi possível enviar a imagem.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await onSave(studio);
      setSavedStudio({ ...studio });
      setNotice('Seção Estúdio atualizada.');
    } catch (saveError) {
      setError(saveError.message || 'Não foi possível salvar a seção Estúdio.');
    } finally {
      setSaving(false);
    }
  };

  const isDirty = JSON.stringify(studio) !== JSON.stringify(savedStudio);

  return (
    <form className="admin-homepage-editor admin-studio-editor" onSubmit={handleSubmit}>
      <div className="admin-homepage-editor__intro">
        <h2 className="font-display">Conteúdo da seção</h2>
        <p>Edite os textos e a imagem exibidos na seção Estúdio da página inicial. A imagem e sua legenda são opcionais.</p>
      </div>

      <label className="admin-studio-editor__field">
        <span className="font-label">Chamada</span>
        <input className="form-control" type="text" value={studio.eyebrow} onChange={(event) => updateField('eyebrow', event.target.value)} maxLength={100} />
      </label>
      <label className="admin-studio-editor__field">
        <span className="font-label">Título</span>
        <input className="form-control" type="text" value={studio.title} onChange={(event) => updateField('title', event.target.value)} required maxLength={300} />
      </label>
      <label className="admin-studio-editor__field">
        <span className="font-label">Texto — parágrafo 1</span>
        <textarea className="form-control" rows={5} value={studio.paragraph_one} onChange={(event) => updateField('paragraph_one', event.target.value)} maxLength={3000} />
      </label>
      <label className="admin-studio-editor__field">
        <span className="font-label">Texto — parágrafo 2</span>
        <textarea className="form-control" rows={5} value={studio.paragraph_two} onChange={(event) => updateField('paragraph_two', event.target.value)} maxLength={3000} />
      </label>

      <div className="admin-studio-editor__image-field">
        <div className="admin-homepage-editor__preview admin-studio-editor__preview">
          {studio.image_url
            ? <img src={studio.image_url} alt={studio.image_alt || 'Prévia da imagem do Estúdio'} />
            : <span>Sem imagem selecionada</span>}
        </div>
        <div className="admin-studio-editor__image-actions">
          <label className="btn btn--outline admin-homepage-editor__upload">
            <Upload size={15} /> {uploading ? 'Enviando...' : studio.image_url ? 'Trocar imagem' : 'Enviar imagem'}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} disabled={uploading || saving} />
          </label>
          {studio.image_url && (
            <button type="button" className="btn btn--outline admin-studio-editor__remove" onClick={() => updateField('image_url', '')} disabled={uploading || saving}>
              <Trash2 size={14} /> Remover imagem
            </button>
          )}
          <p className="admin-homepage-editor__loading">JPEG, PNG ou WebP, até 5 MB.</p>
        </div>
      </div>

      <label className="admin-studio-editor__field">
        <span className="font-label">Descrição da imagem (texto alternativo)</span>
        <input className="form-control" type="text" value={studio.image_alt} onChange={(event) => updateField('image_alt', event.target.value)} maxLength={250} />
      </label>
      <div className="admin-studio-editor__row">
        <label className="admin-studio-editor__field">
          <span className="font-label">Legenda da imagem</span>
          <input className="form-control" type="text" value={studio.image_caption} onChange={(event) => updateField('image_caption', event.target.value)} maxLength={200} />
        </label>
        <label className="admin-studio-editor__field">
          <span className="font-label">Local e ano</span>
          <input className="form-control" type="text" value={studio.image_location} onChange={(event) => updateField('image_location', event.target.value)} maxLength={200} />
        </label>
      </div>

      <div className="admin-homepage-editor__actions">
        <button type="submit" className="btn btn--solid" disabled={saving || uploading || !isDirty}>
          {saving ? 'Salvando...' : 'Salvar seção Estúdio'}
        </button>
      </div>
      {error && <p className="admin-homepage-editor__message is-error" role="alert">{error}</p>}
      {notice && <p className="admin-homepage-editor__message" role="status">{notice}</p>}
    </form>
  );
}
